// Service layer for Vendors CRUD operations
import { supabase } from '../lib/supabase';

export const vendorService = {
  // Get all vendors
  async getAll() {
    const { data, error } = await supabase?.from('vendors')?.select('*')?.order('name', { ascending: true });

    if (error) throw error;

    return data?.map(vendor => ({
      id: vendor?.id,
      name: vendor?.name,
      email: vendor?.email,
      phone: vendor?.phone,
      companyName: vendor?.company_name,
      website: vendor?.website,
      categories: vendor?.categories,
      rating: vendor?.rating,
      totalReviews: vendor?.total_reviews,
      status: vendor?.status,
      bio: vendor?.bio,
      logoUrl: vendor?.logo_url,
      createdAt: vendor?.created_at,
      updatedAt: vendor?.updated_at
    }));
  },

  // Get vendors by category
  async getByCategory(category) {
    const { data, error } = await supabase?.from('vendors')?.select('*')?.contains('categories', [category])?.eq('status', 'active')?.order('rating', { ascending: false });

    if (error) throw error;

    return data?.map(vendor => ({
      id: vendor?.id,
      name: vendor?.name,
      companyName: vendor?.company_name,
      rating: vendor?.rating,
      totalReviews: vendor?.total_reviews,
      categories: vendor?.categories
    }));
  },

  // Get vendors for a specific project
  async getByProjectId(projectId) {
    const { data, error } = await supabase?.from('project_vendors')?.select(`
        *,
        vendors(*)
      `)?.eq('project_id', projectId);

    if (error) throw error;

    return data?.map(pv => ({
      ...pv?.vendors,
      invitedAt: pv?.invited_at,
      respondedAt: pv?.responded_at
    }));
  },

  // Create vendor
  async create(vendorData) {
    const { data, error } = await supabase?.from('vendors')?.upsert({
        name: vendorData?.name,
        email: vendorData?.email,
        phone: vendorData?.phone,
        company_name: vendorData?.companyName,
        website: vendorData?.website,
        categories: vendorData?.categories || [],
        bio: vendorData?.bio,
        logo_url: vendorData?.logoUrl,
        updated_at: new Date()?.toISOString(),
      }, { onConflict: 'email' })?.select()?.single();

    if (error) throw error;

    // If upsert returned null (no-op on conflict with no changes), fetch the existing row
    if (!data) {
      const { data: existing, error: fetchError } = await supabase
        ?.from('vendors')
        ?.select('*')
        ?.eq('email', vendorData?.email)
        ?.single();
      if (fetchError) throw fetchError;
      return existing;
    }

    return data;
  },

  // Link vendor to project
  async linkToProject(projectId, vendorId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('project_vendors')?.insert({
        project_id: projectId,
        vendor_id: vendorId
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update vendor response time
  async markAsResponded(projectId, vendorId) {
    const { data, error } = await supabase?.from('project_vendors')?.update({ responded_at: new Date()?.toISOString() })?.eq('project_id', projectId)?.eq('vendor_id', vendorId)?.select()?.single();

    if (error) throw error;
    return data;
  }
};
